import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

interface CompanyRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegister: (data: {
    companyName: string;
    companyAddress?: string;
    corporateNumber?: string;
    contactPerson: {
      name: string;
      email: string;
      phone: string;
      position: string;
    };
  }) => void;
}

// API検索結果の型定義
interface CompanySearchResult {
  corporateNumber: string;
  name: string;
  address: string;
}

export const CompanyRegistrationModal: React.FC<
  CompanyRegistrationModalProps
> = ({ isOpen, onClose, onRegister }) => {
  const [companyName, setCompanyName] = useState('');
  const [corporateNumber, setCorporateNumber] = useState('');
  const [selectedCompany, setSelectedCompany] =
    useState<CompanySearchResult | null>(null);
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([]);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const [contactInfo, setContactInfo] = useState({
    email: '',
    name: '',
    phone: '',
    position: '',
  });

  // デバウンス用のref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSearchQueryRef = useRef<string>('');

  // 半角記号 → 全角記号のマッピング（useMemoでメモ化）
  const asciiToZenkakuMap = useMemo<Record<string, string>>(
    () => ({
      '!': '！',
      '"': '＂',
      '#': '＃',
      $: '＄',
      '%': '％',
      '&': '＆',
      "'": '＇',
      '(': '（',
      ')': '）',
      '*': '＊',
      '+': '＋',
      ',': '，',
      '-': '－',
      '.': '．',
      '/': '／',
      ':': '：',
      ';': '；',
      '<': '＜',
      '=': '＝',
      '>': '＞',
      '?': '？',
      '@': '＠',
      '[': '［',
      '\\': '＼',
      ']': '］',
      '^': '＾',
      _: '＿',
      '`': '｀',
      '{': '｛',
      '|': '｜',
      '}': '｝',
      '~': '～',
    }),
    []
  );

  // 半角英数字・記号を全角に変換する関数
  const convertHalfWidthToFullWidth = useCallback(
    (text: string): string => {
      let result = '';
      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        const code = char.charCodeAt(0);

        // 半角英字 A-Z (0x41-0x5A) → 全角英字 Ａ-Ｚ (0xFF21-0xFF3A)
        if (code >= 0x41 && code <= 0x5a) {
          result += String.fromCharCode(0xff21 + (code - 0x41));
        }
        // 半角英字 a-z (0x61-0x7A) → 全角英字 ａ-ｚ (0xFF41-0xFF5A)
        else if (code >= 0x61 && code <= 0x7a) {
          result += String.fromCharCode(0xff41 + (code - 0x61));
        }
        // 半角数字 0-9 (0x30-0x39) → 全角数字 ０-９ (0xFF10-0xFF19)
        else if (code >= 0x30 && code <= 0x39) {
          result += String.fromCharCode(0xff10 + (code - 0x30));
        }
        // 半角スペース (0x20) → 全角スペース (0x3000)
        else if (code === 0x20) {
          result += String.fromCharCode(0x3000);
        }
        // 半角記号 → 全角記号に変換
        else if (asciiToZenkakuMap[char] !== undefined) {
          result += asciiToZenkakuMap[char];
        }
        // その他の文字（日本語、全角英数字、全角スペースなど）はそのまま
        else {
          result += char;
        }
      }
      return result;
    },
    [asciiToZenkakuMap]
  );

  // API検索関数
  const searchCompany = useCallback(
    async (query: string) => {
      const trimmedQuery = query.trim();

      // 100エラー: 法人名が未指定
      if (!trimmedQuery) {
        setSearchResults([]);
        setSearchError(null);
        setSearchMessage(null);
        return;
      }

      // 102エラー: 文字数超過の事前検証
      // 日本語: 150文字、英語: 300文字の許容上限
      // 簡易判定: 日本語文字（ひらがな、カタカナ、漢字、全角英数字）が含まれていれば日本語として扱う
      // \u3040-\u309F: ひらがな
      // \u30A0-\u30FF: カタカナ
      // \u4E00-\u9FAF: 漢字
      // \uFF00-\uFFEF: 全角英数字・記号（全角アルファベット、全角数字など）
      const hasJapaneseChars =
        /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\uFF00-\uFFEF]/.test(
          trimmedQuery
        );
      const maxLength = hasJapaneseChars ? 150 : 300;
      if (trimmedQuery.length > maxLength) {
        setSearchResults([]);
        setSearchError(null);
        setSearchMessage(
          `文字数が上限を超えています（${hasJapaneseChars ? '日本語' : '英語'}は${maxLength}文字まで）。手動で入力してください。`
        );
        return;
      }

      // 103エラー: 組織名称のみ指定の事前検証
      // 「株式会社」または「有限会社」のみの場合
      if (trimmedQuery === '株式会社' || trimmedQuery === '有限会社') {
        setSearchResults([]);
        setSearchError(null);
        setSearchMessage(
          '「株式会社」や「有限会社」のみでは検索できません。会社名を含めて入力してください。'
        );
        return;
      }

      // 半角英数字・記号を全角に変換
      const convertedQuery = convertHalfWidthToFullWidth(trimmedQuery);

      // デバッグ: 変換前後の文字列をログ出力
      // eslint-disable-next-line no-console
      console.log(
        '[CompanyRegistrationModal] 変換前:',
        trimmedQuery,
        '変換後:',
        convertedQuery,
        '半角スペース:',
        convertedQuery.includes(' '),
        '全角スペース:',
        convertedQuery.includes('　')
      );

      setIsSearching(true);
      setSearchError(null);
      setSearchMessage(null);

      try {
        // Functions APIを呼び出し
        const searchFunction = httpsCallable<
          { name: string },
          {
            success: boolean;
            data?: CompanySearchResult[];
            error?: string;
            errorCode?: string;
            message?: string;
          }
        >(functions, 'searchCompanyByName');

        // 変換後の文字列をAPIに送信
        const result = await searchFunction({ name: convertedQuery });

        if (result.data.success && result.data.data) {
          setSearchResults(result.data.data);
          // 検索結果が空の場合はメッセージを表示
          if (result.data.data.length === 0) {
            setSearchError(null);
            setSearchMessage(
              '検索に該当する会社が見つかりませんでした。手動で入力してください。'
            );
          } else {
            setSearchMessage(null);
          }
        } else if (result.data.errorCode === '180') {
          // エラーコード180: 結果件数が多すぎ
          setSearchResults([]);
          setSearchError(null);
          setSearchMessage(
            result.data.message ||
              '結果件数が多すぎます。検索条件を絞り込んでください。'
          );
        } else if (result.data.errorCode === '101') {
          // エラーコード101: APIがこの検索条件を受け付けない
          // アルファベットのみ、または日本語とアルファベットが混在する検索が
          // APIの仕様上受け付けられない可能性がある
          setSearchResults([]);
          setSearchError(null);
          setSearchMessage(
            result.data.message ||
              'この検索条件では検索できません。別の検索条件を試すか、手動で入力してください。'
          );
        } else {
          setSearchResults([]);
          setSearchError(result.data.error || '検索に失敗しました');
          setSearchMessage(null);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Company search error:', error);
        setSearchResults([]);
        // エラーメッセージを詳細に表示
        const errorMessage =
          error instanceof Error
            ? error.message
            : '検索中にエラーが発生しました。手動で入力してください。';
        setSearchError(
          errorMessage.includes('API呼び出しエラー')
            ? '検索に該当する会社が見つかりませんでした。手動で入力してください。'
            : errorMessage
        );
        setSearchMessage(null);
      } finally {
        setIsSearching(false);
      }
    },
    [convertHalfWidthToFullWidth]
  );

  // 会社名入力時のデバウンス処理
  useEffect(() => {
    // 前回のタイマーをクリア
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 入力値が変更されていない場合はスキップ
    if (companyName === lastSearchQueryRef.current) {
      return;
    }

    // 空文字列の場合は検索しない
    if (!companyName.trim()) {
      setSearchResults([]);
      setSearchError(null);
      setSearchMessage(null);
      lastSearchQueryRef.current = '';
      return;
    }

    // 選択中の会社名と一致する場合は検索しない
    if (selectedCompany && selectedCompany.name === companyName) {
      return;
    }

    // デバウンス: 500ms後に検索実行
    debounceTimerRef.current = setTimeout(() => {
      lastSearchQueryRef.current = companyName;
      searchCompany(companyName);
    }, 500);

    // クリーンアップ
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [companyName, selectedCompany, searchCompany]);

  // 会社名が変更された時に、選択中の企業と一致しない場合は法人番号をクリア
  useEffect(() => {
    if (selectedCompany && selectedCompany.name !== companyName) {
      setCorporateNumber('');
      setSelectedCompany(null);
    }
  }, [companyName, selectedCompany]);

  const handleCompanySelect = (company: CompanySearchResult) => {
    setCompanyName(company.name);
    setSelectedCompany(company);
    setCorporateNumber(company.corporateNumber);
    setIsCompanyDropdownOpen(false);
    setSearchResults([]);
    setSearchError(null);
    setSearchMessage(null);
    lastSearchQueryRef.current = company.name;
  };

  // 会社名入力フィールドからフォーカスが外れた時にメッセージをクリア
  const handleCompanyNameBlur = () => {
    // ドロップダウンが開いている時はクリアしない（選択可能なため）
    // 少し遅延を入れて、ドロップダウンのクリックイベントが先に処理されるようにする
    setTimeout(() => {
      if (!isCompanyDropdownOpen) {
        setSearchError(null);
        setSearchMessage(null);
      }
    }, 200);
  };

  // 他のフィールドにフォーカスが移った時もメッセージをクリア
  const handleOtherFieldFocus = () => {
    setSearchError(null);
    setSearchMessage(null);
    setIsCompanyDropdownOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      !companyName.trim() ||
      !contactInfo.name.trim() ||
      !contactInfo.email.trim() ||
      !contactInfo.phone.trim() ||
      !contactInfo.position.trim()
    ) {
      return;
    }

    try {
      // eslint-disable-next-line no-console
      console.log('CompanyRegistrationModal: onRegisterを呼び出します');
      await onRegister({
        companyName: companyName.trim(),
        companyAddress: selectedCompany?.address,
        corporateNumber: corporateNumber.trim() || undefined,
        contactPerson: {
          name: contactInfo.name.trim(),
          email: contactInfo.email.trim(),
          phone: contactInfo.phone.trim(),
          position: contactInfo.position.trim(),
        },
      });
      // eslint-disable-next-line no-console
      console.log('CompanyRegistrationModal: onRegister完了');

      // フォームをリセット
      setCompanyName('');
      setCorporateNumber(''); // 内部的に保持（表示はしない）
      setSelectedCompany(null);
      setContactInfo({
        email: '',
        name: '',
        phone: '',
        position: '',
      });
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('CompanyRegistrationModal: onRegisterエラー', error);
      // エラーはhandleCompanyRegisterで処理されるため、ここでは再スローしない
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-purple-800">会社情報登録</h2>
            <p className="text-sm text-purple-600 mt-2">
              会社情報入力後、分析結果が見れます
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 会社名入力 */}
            <div className="space-y-2">
              <label
                htmlFor="company"
                className="block text-sm font-semibold text-purple-700"
              >
                会社名 <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="company"
                  type="text"
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  onFocus={() => setIsCompanyDropdownOpen(true)}
                  onBlur={handleCompanyNameBlur}
                  className="w-full p-3 border-2 border-purple-200 rounded-lg bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400 focus:outline-none"
                  placeholder="会社名を入力または選択してください"
                  required
                />
                {isCompanyDropdownOpen &&
                  (isSearching ||
                    searchResults.length > 0 ||
                    searchError ||
                    searchMessage) && (
                    <div className="absolute z-10 w-full mt-1 bg-white border-2 border-purple-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {isSearching && (
                        <div className="px-4 py-2 text-sm text-gray-600">
                          検索中...
                        </div>
                      )}
                      {!isSearching && searchError && (
                        <div className="px-4 py-2 text-sm text-red-600">
                          {searchError}
                        </div>
                      )}
                      {!isSearching && !searchError && searchMessage && (
                        <div className="px-4 py-2 text-sm text-gray-600">
                          {searchMessage}
                        </div>
                      )}
                      {!isSearching &&
                        !searchError &&
                        !searchMessage &&
                        searchResults.length > 0 && (
                          <>
                            {searchResults.map((company, index) => (
                              <button
                                key={`${company.corporateNumber}-${index}`}
                                type="button"
                                onClick={() => handleCompanySelect(company)}
                                className="w-full px-4 py-2 text-left hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                              >
                                {company.name}
                              </button>
                            ))}
                          </>
                        )}
                    </div>
                  )}
              </div>
            </div>

            {selectedCompany && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-purple-700">
                  住所
                </label>
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                  <p className="text-purple-800 text-sm">
                    {selectedCompany.address}
                  </p>
                </div>
              </div>
            )}

            {/* 担当者情報 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-semibold text-purple-700"
                >
                  担当者氏名 <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={contactInfo.name}
                  onChange={e =>
                    setContactInfo(prev => ({ ...prev, name: e.target.value }))
                  }
                  onFocus={handleOtherFieldFocus}
                  className="w-full p-3 border-2 border-purple-200 rounded-lg bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400 focus:outline-none"
                  placeholder="山田 太郎"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="position"
                  className="block text-sm font-semibold text-purple-700"
                >
                  役職 <span className="text-red-500">*</span>
                </label>
                <input
                  id="position"
                  type="text"
                  value={contactInfo.position}
                  onChange={e =>
                    setContactInfo(prev => ({
                      ...prev,
                      position: e.target.value,
                    }))
                  }
                  onFocus={handleOtherFieldFocus}
                  className="w-full p-3 border-2 border-purple-200 rounded-lg bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400 focus:outline-none"
                  placeholder="人事部長"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-purple-700"
              >
                メールアドレス <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={contactInfo.email}
                onChange={e =>
                  setContactInfo(prev => ({ ...prev, email: e.target.value }))
                }
                onFocus={handleOtherFieldFocus}
                className="w-full p-3 border-2 border-purple-200 rounded-lg bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400 focus:outline-none"
                placeholder="yamada@example.com"
                required
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="phone"
                className="block text-sm font-semibold text-purple-700"
              >
                電話番号 <span className="text-red-500">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                value={contactInfo.phone}
                onChange={e =>
                  setContactInfo(prev => ({ ...prev, phone: e.target.value }))
                }
                onFocus={handleOtherFieldFocus}
                className="w-full p-3 border-2 border-purple-200 rounded-lg bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400 focus:outline-none"
                placeholder="03-1234-5678"
                required
              />
            </div>

            {/* ボタン */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border-2 border-purple-200 text-purple-600 rounded-lg hover:bg-purple-50 bg-transparent transition-colors"
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="px-8 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg font-semibold transition-colors"
              >
                完了
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
